import * as React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@shared/lib/utils';
import { useLocalStorage } from '@shared/hooks/useLocalStorage';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useAppConfig } from '@shared/app/app-config';
import {
  isNavSubGroup,
  matchesPrefix,
  visibleNavItems,
  type NavChildItem,
  type NavGroupItem,
  type NavItem,
  type NavLinkItem,
  type NavSubGroupItem,
} from './nav-config';

/** Shared row styling for a top-level (parent) nav item, active or not. */
const parentLinkClass = (isActive: boolean) =>
  cn(
    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-150',
    isActive
      ? 'bg-sidebar-accent font-medium text-white'
      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
  );

/** Shared row styling for an indented child nav item, active or not. */
const childLinkClass = (isActive: boolean) =>
  cn(
    'flex items-center gap-3 rounded-lg border-l-2 py-2 pl-[26px] pr-3 text-sm transition-colors duration-150',
    isActive
      ? 'border-sidebar-primary bg-sidebar-accent/60 font-medium text-sidebar-accent-foreground'
      : 'border-transparent text-sidebar-foreground/70 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground',
  );

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <>
      <div className="my-3 border-t border-sidebar-border" />
      <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider text-sidebar-muted-foreground">
        {children}
      </p>
    </>
  );
}

function NavLinkRow({ item, onNavigate }: { item: NavLinkItem; onNavigate?: () => void }) {
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) => parentLinkClass(isActive)}
    >
      <item.icon className="size-4 shrink-0" />
      {item.label}
    </NavLink>
  );
}

/** One indented link inside a group (or, one level deeper, a sub-group). */
function NavChildRow({
  child,
  nested = false,
  onNavigate,
}: {
  child: NavChildItem;
  nested?: boolean;
  onNavigate?: () => void;
}) {
  const { pathname } = useLocation();
  const alsoActive = child.activePrefixes ? matchesPrefix(pathname, child.activePrefixes) : false;
  return (
    <NavLink
      to={child.to}
      end={child.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(childLinkClass(isActive || alsoActive), nested && 'py-1.5 pl-[46px]')
      }
    >
      <child.icon className="size-4 shrink-0" />
      {child.label}
    </NavLink>
  );
}

/**
 * A sub-group inside a group (Finance → Asset Management, Payroll).
 *
 * Expanded only while one of its screens is open — never by a stored toggle —
 * so the rail shows one nested list at most. The header opens the first child
 * the user may reach, which is what makes the sub-group active.
 */
function NavSubGroupRow({ item, onNavigate }: { item: NavSubGroupItem; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active = matchesPrefix(pathname, item.matchPrefixes);
  const first = item.children[0];

  return (
    <div>
      <button
        type="button"
        aria-expanded={active}
        onClick={() => {
          if (!active && first) {
            navigate(first.to);
            onNavigate?.();
          }
        }}
        className={cn(
          childLinkClass(false),
          'w-full',
          active && 'font-medium text-sidebar-foreground',
        )}
      >
        <item.icon className="size-4 shrink-0" />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronRight
          className={cn(
            'size-3.5 shrink-0 text-sidebar-foreground/50 transition-transform duration-200',
            active && 'rotate-90',
          )}
        />
      </button>
      {active ? (
        <div className="mt-0.5 space-y-0.5">
          {item.children.map((child) => (
            <NavChildRow key={child.to} child={child} nested onNavigate={onNavigate} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Collapsible group. Only rendered when at least one child is permitted. */
function NavGroupRow({ item, onNavigate }: { item: NavGroupItem; onNavigate?: () => void }) {
  const location = useLocation();
  const [open, setOpen] = useLocalStorage(item.storageKey, true);
  const active = matchesPrefix(location.pathname, item.matchPrefix);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className={cn(parentLinkClass(active), 'w-full')}
      >
        <item.icon className="size-4 shrink-0" />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-sidebar-foreground/50 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>
      {open ? (
        <div className="mt-1 space-y-1">
          {item.children.map((child) =>
            isNavSubGroup(child) ? (
              <NavSubGroupRow key={child.label} item={child} onNavigate={onNavigate} />
            ) : (
              <React.Fragment key={child.to}>
                {child.heading ? (
                  <p className="px-3 pb-0.5 pl-[28px] pt-2 text-2xs font-semibold uppercase tracking-wider text-sidebar-muted-foreground">
                    {child.heading}
                  </p>
                ) : null}
                <NavChildRow child={child} onNavigate={onNavigate} />
              </React.Fragment>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

function NavSection({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  return (
    <>
      {items.map((item) =>
        item.type === 'link' ? (
          <NavLinkRow key={item.to} item={item} onNavigate={onNavigate} />
        ) : (
          <NavGroupRow key={item.label} item={item} onNavigate={onNavigate} />
        ),
      )}
    </>
  );
}

export interface SidebarProps {
  /** Called after a nav link is activated — used to close the mobile drawer. */
  onNavigate?: () => void;
}

/**
 * Application navigation, from the application's own `AppConfig.navigation`.
 *
 * Every destination is filtered through the centralized permission checks, and
 * a section whose items are all hidden is not rendered at all. Hiding a link is
 * only an affordance — each route carries its own guard, and the backend
 * re-checks every request.
 */
export function Sidebar({ onNavigate }: SidebarProps) {
  const { name, navigation, usesPermissions } = useAppConfig();
  const { can, canAny, hasScope } = usePermissions();
  const check = React.useMemo(
    () =>
      usesPermissions
        ? { can, canAny, hasScope }
        : { can: () => true, canAny: () => true, hasScope: () => true },
    [can, canAny, hasScope, usesPermissions],
  );

  const sections = React.useMemo(
    () =>
      navigation
        .map((section) => ({ ...section, items: visibleNavItems(section.items, check) }))
        .filter((section) => section.items.length > 0),
    [navigation, check],
  );

  return (
    <>
      <div className="flex h-16 shrink-0 items-center gap-2.5 px-6">
        <img src="/favicon.svg" alt="" className="size-8 rounded-lg" />
        <span className="min-w-0 leading-tight">
          <span className="block text-sm font-semibold tracking-tight text-sidebar-foreground">
            HEXAUNITY
          </span>
          <span className="block truncate text-xs text-sidebar-muted-foreground">{name}</span>
        </span>
      </div>

      <nav className="grow space-y-1 px-3 py-4" aria-label={`${name} navigation`}>
        {sections.map((section, index) => (
          <React.Fragment key={section.heading ?? `section-${index}`}>
            {section.heading ? <SectionHeading>{section.heading}</SectionHeading> : null}
            <NavSection items={section.items} onNavigate={onNavigate} />
          </React.Fragment>
        ))}
      </nav>
    </>
  );
}
